import $ from 'jquery';
import Swiper from 'swiper';
import { Navigation, Autoplay, Pagination, Parallax, Thumbs } from 'swiper/modules';
import 'swiper/swiper-bundle.css';
import './sass/style.scss';
import './js/app';
import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';

window.Alpine = Alpine;
Alpine.plugin(collapse);
Alpine.start();

function initProductGallerySlider() {
    const mainGallery = document.querySelector('.js-product-gallery-main');

    if (!mainGallery) {
        return;
    }

    const thumbsGallery = document.querySelector('.js-product-gallery-thumbs');
    const variationForm = document.querySelector('form.variations_form');
    const openVideoButton = mainGallery.querySelector('[data-gallery-open-video]');
    const MOBILE_MAX_WIDTH = 1023;
    let resizeRafId = null;
    let openVideoButtonHandler = null;
    let thumbsSwiper = null;
    let mainSwiper = null;

    const normalizeGalleryUrl = (url) => {
        if (typeof url !== 'string' || !url.trim()) {
            return '';
        }

        try {
            const parsed = new URL(url, window.location.origin);
            parsed.hash = '';
            return `${parsed.origin}${parsed.pathname}`;
        } catch (error) {
            return url.split('#')[0].split('?')[0];
        }
    };

    const getFirstImageSlideIndex = () => {
        const slides = Array.from(mainGallery.querySelectorAll('.swiper-slide'));
        return slides.findIndex((slide) => slide.querySelector('.woocommerce-product-gallery__image img'));
    };

    const findVariationImageSlideIndex = (variation) => {
        if (!variation || typeof variation !== 'object') {
            return -1;
        }

        const variationImage = variation.image || {};
        const sourceCandidates = [
            variationImage.full_src,
            variationImage.src,
            variationImage.gallery_thumbnail_src,
        ]
            .map(normalizeGalleryUrl)
            .filter(Boolean);

        if (!sourceCandidates.length) {
            return -1;
        }

        const slides = Array.from(mainGallery.querySelectorAll('.swiper-slide'));

        return slides.findIndex((slide) => {
            const imageEl = slide.querySelector('.woocommerce-product-gallery__image img');
            if (!(imageEl instanceof HTMLImageElement)) {
                return false;
            }

            const slideSources = [
                imageEl.currentSrc,
                imageEl.getAttribute('src'),
                imageEl.getAttribute('data-src'),
                imageEl.getAttribute('data-large_image'),
            ]
                .map(normalizeGalleryUrl)
                .filter(Boolean);

            return slideSources.some((src) => sourceCandidates.includes(src));
        });
    };

    const syncGalleryVideosPlayback = () => {
        if (!mainSwiper) {
            return;
        }

        const videos = mainGallery.querySelectorAll('video');

        videos.forEach((videoElement) => {
            const slide = videoElement.closest('.swiper-slide');
            const isActive = slide?.classList.contains('swiper-slide-active');

            if (isActive) {
                const playPromise = videoElement.play();
                if (playPromise && typeof playPromise.catch === 'function') {
                    playPromise.catch(() => {});
                }
            } else {
                videoElement.pause();
            }
        });

        if (openVideoButton) {
            const videoSlideIndex = Array.from(mainGallery.querySelectorAll('.swiper-slide')).findIndex(
                (slide) => slide.querySelector('video'),
            );
            const shouldHideButton = videoSlideIndex < 0 || videoSlideIndex === mainSwiper.activeIndex;
            openVideoButton.classList.toggle('is-hidden', shouldHideButton);
        }
    };

    const moveToVariationImageSlide = (variation) => {
        if (!mainSwiper) {
            return;
        }

        let targetIndex = findVariationImageSlideIndex(variation);

        if (targetIndex < 0) {
            targetIndex = getFirstImageSlideIndex();
        }

        if (targetIndex >= 0 && targetIndex !== mainSwiper.activeIndex) {
            mainSwiper.slideTo(targetIndex, 0);
        }

        syncGalleryVideosPlayback();
    };

    const bindVariationEvents = () => {
        if (!variationForm) {
            return;
        }

        const $variationForm = $(variationForm);
        $variationForm.off('.productGalleryVideoFix');
        $variationForm.on('found_variation.productGalleryVideoFix', (event, variation) => {
            requestAnimationFrame(() => {
                moveToVariationImageSlide(variation);
            });
        });
        $variationForm.on('show_variation.productGalleryVideoFix', (event, variation) => {
            requestAnimationFrame(() => {
                moveToVariationImageSlide(variation);
            });
        });
        $variationForm.on('reset_image.productGalleryVideoFix hide_variation.productGalleryVideoFix', () => {
            requestAnimationFrame(() => {
                moveToVariationImageSlide(null);
            });
        });
    };

    const unbindVariationEvents = () => {
        if (!variationForm) {
            return;
        }

        $(variationForm).off('.productGalleryVideoFix');
    };

    const bindOpenVideoButton = () => {
        if (!openVideoButton || openVideoButtonHandler) {
            return;
        }

        openVideoButtonHandler = (event) => {
            event.preventDefault();

            if (!mainSwiper) {
                return;
            }

            const videoSlideIndex = Array.from(mainGallery.querySelectorAll('.swiper-slide')).findIndex(
                (slide) => slide.querySelector('video'),
            );

            if (videoSlideIndex >= 0) {
                mainSwiper.slideTo(videoSlideIndex, 300);
                syncGalleryVideosPlayback();
            }
        };

        openVideoButton.addEventListener('click', openVideoButtonHandler);
    };

    const unbindOpenVideoButton = () => {
        if (!openVideoButton || !openVideoButtonHandler) {
            return;
        }

        openVideoButton.removeEventListener('click', openVideoButtonHandler);
        openVideoButtonHandler = null;
        openVideoButton.classList.remove('is-hidden');
    };

    const initMobileGallery = () => {
        if (mainSwiper) {
            return;
        }

        if (thumbsGallery) {
            thumbsSwiper = new Swiper(thumbsGallery, {
                slidesPerView: 'auto',
                spaceBetween: 8,
                watchSlidesProgress: true,
                watchOverflow: true,
                observer: true,
                observeParents: true,
            });
        }

        const productSwiperOptions = {
            modules: [Pagination, Thumbs],
            slidesPerView: 1,
            spaceBetween: 0,
            roundLengths: true,
            watchOverflow: true,
            observer: true,
            observeParents: true,
        };

        const paginationElement = mainGallery.querySelector('.swiper-pagination');
        if (paginationElement) {
            productSwiperOptions.pagination = {
                el: paginationElement,
                clickable: true,
            };
        }

        if (thumbsSwiper) {
            productSwiperOptions.thumbs = {
                swiper: thumbsSwiper,
            };
        }

        mainSwiper = new Swiper(mainGallery, productSwiperOptions);
        bindOpenVideoButton();
        bindVariationEvents();
        syncGalleryVideosPlayback();
        mainSwiper.on('slideChange', syncGalleryVideosPlayback);
        mainSwiper.on('transitionStart', syncGalleryVideosPlayback);
    };

    const destroyMobileGallery = () => {
        unbindVariationEvents();
        unbindOpenVideoButton();

        if (mainSwiper) {
            mainSwiper.off('slideChange', syncGalleryVideosPlayback);
            mainSwiper.off('transitionStart', syncGalleryVideosPlayback);
            mainSwiper.destroy(true, true);
            mainSwiper = null;
        }

        if (thumbsSwiper) {
            thumbsSwiper.destroy(true, true);
            thumbsSwiper = null;
        }

        mainGallery.querySelectorAll('video').forEach((videoElement) => {
            const playPromise = videoElement.play();
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {});
            }
        });
    };

    const syncGalleryMode = () => {
        if (window.innerWidth > MOBILE_MAX_WIDTH) {
            destroyMobileGallery();
            return;
        }

        initMobileGallery();
    };

    syncGalleryMode();

    window.addEventListener('resize', () => {
        if (resizeRafId) {
            window.cancelAnimationFrame(resizeRafId);
        }

        resizeRafId = window.requestAnimationFrame(() => {
            syncGalleryMode();
        });
    });
}

initProductGallerySlider();

function syncVariationAttributesToUrl() {
    const variationForms = Array.from(document.querySelectorAll('form.variations_form'));
    if (!variationForms.length || typeof window.history.replaceState !== 'function') {
        return;
    }

    const updateUrlFromForm = (formElement) => {
        if (!(formElement instanceof HTMLFormElement)) {
            return;
        }

        const attributeFields = Array.from(formElement.querySelectorAll('select[name^="attribute_"]'));
        if (!attributeFields.length) {
            return;
        }

        const currentUrl = new URL(window.location.href);

        attributeFields.forEach((field) => {
            const key = field.name;
            const value = typeof field.value === 'string' ? field.value.trim() : '';

            if (!key) {
                return;
            }

            if (value) {
                currentUrl.searchParams.set(key, value);
            } else {
                currentUrl.searchParams.delete(key);
            }
        });

        const nextQuery = currentUrl.searchParams.toString();
        const nextUrl = `${currentUrl.pathname}${nextQuery ? `?${nextQuery}` : ''}${currentUrl.hash}`;
        const existingUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

        if (nextUrl !== existingUrl) {
            window.history.replaceState({}, '', nextUrl);
        }
    };

    variationForms.forEach((formElement) => {
        const sync = () => updateUrlFromForm(formElement);

        formElement.addEventListener('change', (event) => {
            const target = event.target;
            if (!(target instanceof Element) || !target.matches('select[name^="attribute_"], input[name^="attribute_"]')) {
                return;
            }

            sync();
        });

        const $form = $(formElement);
        $form.on(
            'wc_variation_form.ovaliaUrlSync woocommerce_variation_has_changed.ovaliaUrlSync found_variation.ovaliaUrlSync show_variation.ovaliaUrlSync hide_variation.ovaliaUrlSync reset_data.ovaliaUrlSync',
            sync,
        );

        requestAnimationFrame(sync);
    });
}

syncVariationAttributesToUrl();

const categoryTilesSwiper = new Swiper('.categories-tiles-swiper', {
    modules: [Navigation],
    slidesPerView: 'auto',
    spaceBetween: 16,
    navigation: {
        nextEl: '.categories-tiles-swiper-nav-right',
        prevEl: '.categories-tiles-swiper-nav-left',
    },
});

function updateSwiperNavVisibility() {
    const navContainer = document.querySelector('.tiles-nav-button-container');
    const prevBtn = document.querySelector('.categories-tiles-swiper-nav-left');
    const nextBtn = document.querySelector('.categories-tiles-swiper-nav-right');

    const bothDisabled = prevBtn?.hasAttribute('disabled') && nextBtn?.hasAttribute('disabled');

    if (navContainer) {
        navContainer.style.display = bothDisabled ? 'none' : '';
    }
}

categoryTilesSwiper.on('slideChange update resize', updateSwiperNavVisibility);

updateSwiperNavVisibility();

const topbarSlider = new Swiper('.topbar-swiper', {
    loop: true,
    modules: [Navigation, Autoplay],
    navigation: {
        nextEl: '.topbar-swiper-right',
        prevEl: '.topbar-swiper-left',
    },
    autoplay: {
        delay: 5000,
    },
});

function initSwiper() {
    if (document.getElementById('hero-static')) {
        document.getElementById('hero-static').style.display = 'none';
    }
    if (document.getElementById('hero-slider')) {
        document.getElementById('hero-slider').style.display = 'block';
    }
    const homepageMainSlider = new Swiper('.homepage-main-slider', {
        loop: true,
        speed: 800,
        modules: [Navigation, Pagination, Autoplay, Parallax],
        parallax: {
            enabled: true,
        },
        navigation: {
            nextEl: '.homepage-main-slider-right',
            prevEl: '.homepage-main-slider-left',
        },
        pagination: {
            el: '.homepage-main-slider-pagination',
            clickable: true,
        },
        autoplay: {
            delay: 5000,
        },
    });
}

$(window).on('load', initSwiper);

$(document).on('change', '#select_all', function () {
    if ($(this).is(':checked')) {
        $(this).parent().parent().find('input[type=checkbox]').prop('checked', true);
    } else {
        $(this).parent().parent().find('input[type=checkbox]').prop('checked', false);
    }
});

$('#show_coupon_form').on('click', () => {
    $('#coupon_form').slideToggle();
});

$(document).on('change', '.variation-switcher', function () {
    const cart_item_key = $(this).data('cart-item-key');
    const variation_id = $(this).find('input[type="radio"]:checked').val();

    $.ajax({
        type: 'POST',
        url: jsData.ajaxUrl,
        data: {
            action: 'change_variation_in_cart',
            cart_item_key,
            variation_id,
        },
        success(response) {
            if (response.success) {
                $(document.body).trigger('update_checkout');
            } else {
                alert('Error: Unable to change variation.');
            }
        },
    });
});

$('a.Button.slideable').on('click', function (e) {
    e.preventDefault();

    const target = $(this).attr('href');
    const hashIndex = target.indexOf('#');

    if (hashIndex !== -1) {
        const targetId = target.substring(hashIndex);

        if ($(targetId).length) {
            const headerHeight = $('.Header').outerHeight();
            const targetPosition = $(targetId).offset().top - headerHeight;

            $('html, body').animate(
                {
                    scrollTop: targetPosition,
                },
                400,
            );
        }
    }
});

const initializeCounters = (root = document) => {
    const counters = root.querySelectorAll('[data-component="counter"]');
    const addZero = (n) => (n < 10 ? `0${n}` : n);
    const getDuration = (type) => {
        const v = parseInt(type, 10);
        return type.endsWith('m') ? v * 60 * 1000 : v * 60 * 60 * 1000;
    };
    const formatBlocks = ({ h, m, s }, showH) =>
        [
            showH &&
                `
        <div class="flex items-center w-[56px] h-[40px] rounded-[2px] bg-white/10 flex-col justify-center !text-white text-[12px] md:text-base font-normal">
            ${h}
            <div class="!text-[12px] ml-[2px] md:ml-0 !font-medium !text-white/60 block md:h-[10px] md:leading-[0px] md:mt-1">Godzin</div>
        </div>`,
            `
        <div class="flex items-center w-[56px] h-[40px] rounded-[2px] bg-white/10 flex-col justify-center !text-white text-[12px] md:text-base font-normal">
            ${addZero(m)}
            <div class="!text-[12px] ml-[2px] md:ml-0 !font-medium !text-white/60 block md:h-[10px] md:leading-[0px] md:mt-1">Minut</div>
        </div>`,
            `
        <div class="flex items-center w-[56px] h-[40px] rounded-[2px] bg-white/10 flex-col justify-center !text-white text-[12px] md:text-base font-normal">
            ${addZero(s)}
            <div class="!text-[12px] ml-[2px] md:ml-0 !font-medium !text-white/60 block md:h-[10px] md:leading-[0px] md:mt-1">Sekund</div>
        </div>`,
        ]
            .filter(Boolean)
            .join('');

    counters.forEach((counter) => {
        const type = counter.dataset.type || '24h';
        let endTime;
        if (type === '24h') {
            const now = new Date();
            const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
            endTime = midnight.getTime();
        } else {
            endTime = Date.now() + getDuration(type);
        }
        const showHours = !type.endsWith('m');

        const tick = () => {
            const diff = endTime - Date.now();
            if (diff <= 0) {
                clearInterval(timerId);
                counter.innerHTML = '';
                return;
            }
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            counter.innerHTML = formatBlocks({ h, m, s }, showHours);
        };

        const timerId = setInterval(tick, 1000);
        tick();
    });
};

window.initializeCounters = initializeCounters;

document.addEventListener('DOMContentLoaded', () => {
    initializeCounters();

    const faqItemBtns = document.querySelectorAll('.faq-item-btn');
    const faqSections = document.querySelectorAll('.faq-section');

    function addActiveClass(button) {
        faqItemBtns.forEach((btn) => btn.classList.remove('active-faq-item-btn'));
        faqSections.forEach((section) => {
            section.classList.add('hidden');
            section.classList.remove('hidden');
        });

        const sectionId = button.getAttribute('href').replace('#', '').trim();

        faqSections.forEach((section) => {
            section.classList.add('hidden');
            if (section.getAttribute('data-id') === sectionId) {
                section.classList.remove('hidden');
            }
        });

        button.classList.add('active-faq-item-btn');

        $('html, body').animate({
            scrollTop: $(`[data-id="${sectionId}"]`).offset().top - $('.Header').height() - 20,
        });
    }

    faqItemBtns.forEach((button) => {
        button.addEventListener('click', (event) => {
            event.preventDefault();
            addActiveClass(button);
        });
    });

    window.addEventListener('scroll', function () {
        const scrollPosition = window.scrollY + window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollPercentage = (scrollPosition / documentHeight) * 100;

        const button = document.getElementById('go-top-button');
        if (!button) {
            return;
        }
        if (scrollPercentage >= 95) {
            button.style.display = 'flex';
        } else {
            button.style.display = 'none';
        }
    });

    $('#go-top-button').on('click', () => {
        $('html, body').animate({ scrollTop: 0 });
        return false;
    });
});

// let current = Math.floor(Math.random() * 61) + 10;

// function updatePeopleCounter() {
//     const change = Math.floor(Math.random() * 3) + 1;
//     const direction = Math.random() < 0.5 ? -1 : 1;
//     let next = current + change * direction;
//     if (next < 10) next = 10;
//     if (next > 70) next = 70;
//     current = next;
//     $('#single-product-people-view-counter').html(current);
//     const timeout = Math.floor(Math.random() * 3000) + 8000;
//     setTimeout(updatePeopleCounter, timeout);
// }

// $('#single-product-people-view-counter').html(current);
// updatePeopleCounter();

$(window).load(function () {
    const faqLinks = $('.flex.flex-col a[href^="#"]');
    const faqSections = $('.faq-section');
    const scrollOffset = 40;

    faqLinks.on('click', function (e) {
        e.preventDefault();
        const targetId = $(this).attr('href');
        const $target = $(targetId);
        if ($target.length) {
            $('html, body').animate(
                {
                    scrollTop: $target.offset().top - scrollOffset,
                },
                400,
            );
        }
    });

    // Podświetlanie aktywnego linku podczas scrola
    $(window).on('scroll', function () {
        const scrollPosition = $(window).scrollTop() + scrollOffset + 60;

        faqSections.each(function () {
            const sectionTop = $(this).offset().top;
            const sectionBottom = sectionTop + $(this).outerHeight();
            const sectionId = $(this).attr('id');
            if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
                faqLinks.removeClass('active-faq-item-btn');
                faqLinks.filter(`[href="#${sectionId}"]`).addClass('active-faq-item-btn');
            }
        });
    });
});

const mobileBlogSlider = new Swiper('.latest-posts-swiper', {
    modules: [Pagination],
    slidesPerView: 1,
    spaceBetween: 16,
    breakpoints: {
        768: {
            slidesPerView: 3,
        },
    },
    pagination: { el: '.blog-pagination', clickable: true },
});

if (window.innerWidth < 768) {
    document.querySelectorAll('[data-collapsible]').forEach((section) => {
        const toggle = section.querySelector('.footer-toggle');
        const content = section.querySelector('.footer-section-content');
        if (!toggle) return;

        toggle.addEventListener('click', () => {
            const expanded = toggle.getAttribute('aria-expanded') === 'true';
            toggle.setAttribute('aria-expanded', !expanded);
            content.classList.toggle('hidden');
            toggle.querySelector('.icon-plus').classList.toggle('hidden');
            toggle.querySelector('.icon-minus').classList.toggle('hidden');
        });
    });
}

function findSwatchButton(target) {
    if (!(target instanceof Element)) {
        return null;
    }

    return target.closest('.wc-swatch');
}

function findSwatchContainer(target) {
    if (!(target instanceof Element)) {
        return null;
    }

    return target.closest('.wc-swatches');
}

function updateLoopProductImageFromSwatch(buttonEl) {
    if (!(buttonEl instanceof Element)) {
        return;
    }

    const productEl = buttonEl.closest('.product');
    if (!(productEl instanceof Element)) {
        return;
    }

    const imageEl = productEl.querySelector('img');
    const src = buttonEl.getAttribute('data-image') || '';
    const srcset = buttonEl.getAttribute('data-srcset') || '';

    if (!(imageEl instanceof HTMLImageElement) || !src) {
        return;
    }

    imageEl.setAttribute('src', src);
    if (srcset) {
        imageEl.setAttribute('srcset', srcset);
    } else {
        imageEl.removeAttribute('srcset');
    }

    const siblings = buttonEl.parentElement
        ? buttonEl.parentElement.querySelectorAll('.wc-swatch')
        : [];
    siblings.forEach((node) => node.classList.remove('is-active'));
    buttonEl.classList.add('is-active');
}

function swallowSwatchEvent(event) {
    const swatchButton = findSwatchButton(event.target);
    const swatchContainer = swatchButton
        ? findSwatchContainer(swatchButton)
        : findSwatchContainer(event.target);

    if (!swatchContainer) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
    }

    if (swatchButton && event.type === 'click') {
        updateLoopProductImageFromSwatch(swatchButton);
    }
}

[window, document].forEach((target) => {
    target.addEventListener('pointerdown', swallowSwatchEvent, true);
    target.addEventListener('mousedown', swallowSwatchEvent, true);
    target.addEventListener(
        'touchstart',
        function (event) {
            swallowSwatchEvent(event);
        },
        { capture: true, passive: false },
    );
    target.addEventListener('click', swallowSwatchEvent, true);
});

// (function () {
//     // Guard - tylko jedna inicjalizacja
//     if (window.__verenzaInfiniteInit) return;
//     window.__verenzaInfiniteInit = true;

//     if (!('IntersectionObserver' in window)) return;

//     const body = document.body;
//     body.classList.add('js-infinite-enabled');

//     // Siatka i sentinel
//     const grid = document.querySelector('.products');
//     const sentinel = document.getElementById('infinite-sentinel');
//     if (!grid || !sentinel) return;

//     // Helpery
//     const STORAGE_KEY = `verenza:archiveState:${location.pathname}${location.search}`;

//     // Zwraca listę kart-produktów dla danego root
//     function getItems(root) {
//         const scope = root || document;
//         const productsEl = scope.querySelector('.products') || scope;
//         // dzieci .products, które mają klasę .product (u Ciebie to <div class="LoopProduct ... product ...">)
//         return productsEl.querySelectorAll(':scope > .product');
//     }

//     // ID produktu z karty
//     function getProductId(el) {
//         if (!el) return null;
//         // preferuj data-product-id na karcie (jeśli dodasz)
//         const d = el.getAttribute('data-product-id');
//         if (d) return d;
//         // klasa post-123 na karcie
//         const m = el.className && el.className.match(/\bpost-(\d+)\b/);
//         if (m) return m[1];
//         // awaryjnie: spróbuj znaleźć w swatchach
//         const sw = el.querySelector('[data-product-id]');
//         if (sw) return sw.getAttribute('data-product-id');
//         return null;
//     }

//     // href produktu (do dodatkowego dedupe)
//     function getProductHref(el) {
//         const a = el.querySelector('a.woocommerce-LoopProduct-link, a[href*="/produkt/"]');
//         return a ? a.getAttribute('href') : null;
//     }

//     // Zestawy antyduplikacyjne
//     let seenIds = new Set();
//     let seenHrefs = new Set();

//     // Seed z aktualnej siatki i od razu oczyść ewentualne duplikaty obecne w DOM
//     Array.from(getItems(document)).forEach((item) => {
//         const id = getProductId(item);
//         const href = getProductHref(item);
//         const hasId = id && seenIds.has(id);
//         const hasHref = href && seenHrefs.has(href);
//         if (hasId || hasHref) {
//             item.remove();
//         } else {
//             if (id) seenIds.add(id);
//             if (href) seenHrefs.add(href);
//         }
//     });

//     // Paginacja -> „następny” URL: pierwszy link po .is-current w .Pagination-pages
//     function getNextUrl(doc) {
//         const root = doc || document;
//         const pages = root.querySelector('.Pagination .Pagination-pages');
//         if (!pages) return null;
//         const current = pages.querySelector('.is-current');
//         if (!current) return null;
//         let node = current.nextElementSibling;
//         while (node && node.nodeType === 1 && node.tagName !== 'A') node = node.nextElementSibling;
//         return node && node.tagName === 'A' ? node.getAttribute('href') : null;
//     }

//     // Restore z sessionStorage
//     let nextUrl = null;
//     let loading = false;
//     let done = false;

//     try {
//         const raw = sessionStorage.getItem(STORAGE_KEY);
//         if (raw) {
//             const saved = JSON.parse(raw);
//             if (saved && saved.html && Date.now() - (saved.ts || 0) < 30 * 60 * 1000) {
//                 grid.innerHTML = saved.html;
//                 nextUrl = saved.nextUrl || null;

//                 // Przywróć zbiory widzianych, jeśli były zapisane
//                 seenIds = new Set(saved.seenIds || []);
//                 seenHrefs = new Set(saved.seenHrefs || []);

//                 // Dodatkowo przeleć DOM i usuń ewentualne duplikaty
//                 Array.from(getItems(document)).forEach((item) => {
//                     const id = getProductId(item);
//                     const href = getProductHref(item);
//                     const dup = (id && seenIds.has(id)) || (href && seenHrefs.has(href));
//                     if (dup) {
//                         // pierwszy egzemplarz powinien trafić do setu - więc jeśli nie ma,
//                         // dodaj i zostaw, a duplikaty usuń
//                         if (id && !seenIds.has(id)) seenIds.add(id);
//                         if (href && !seenHrefs.has(href)) seenHrefs.add(href);
//                     }
//                 });

//                 if (!nextUrl) {
//                     done = true;
//                     sentinel.style.display = 'none';
//                 }
//                 requestAnimationFrame(() => window.scrollTo(0, saved.scrollY || 0));
//             }
//         }
//     } catch (_) {}

//     if (!nextUrl) {
//         nextUrl = getNextUrl(document);
//         done = !nextUrl;
//         if (done) sentinel.style.display = 'none';
//     }

//     // Zapis stanu przed wyjściem lub kliknięciem w kartę
//     function saveState() {
//         try {
//             const payload = {
//                 html: grid.innerHTML,
//                 nextUrl,
//                 scrollY: window.scrollY,
//                 seenIds: Array.from(seenIds),
//                 seenHrefs: Array.from(seenHrefs),
//                 ts: Date.now(),
//             };
//             sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
//         } catch (_) {}
//     }

//     // Klik w produkty -> zapisz stan
//     grid.addEventListener(
//         'click',
//         (e) => {
//             const a = e.target.closest('a');
//             if (!a) return;
//             saveState();
//         },
//         { capture: true },
//     );

//     window.addEventListener('beforeunload', saveState);

//     // Infinite scroll z antyduplikacją
//     const io = new IntersectionObserver(
//         async (entries) => {
//             const entry = entries[0];
//             if (!entry || !entry.isIntersecting || loading || done) return;
//             if (!nextUrl) {
//                 done = true;
//                 sentinel.style.display = 'none';
//                 io.disconnect();
//                 return;
//             }

//             loading = true;
//             try {
//                 const res = await fetch(nextUrl, {
//                     headers: { 'X-Requested-With': 'XMLHttpRequest' },
//                 });
//                 if (!res.ok) throw new Error(`HTTP ${res.status}`);
//                 const html = await res.text();

//                 const parser = new DOMParser();
//                 const doc = parser.parseFromString(html, 'text/html');

//                 const candidates = Array.from(getItems(doc));
//                 const frag = document.createDocumentFragment();

//                 for (const item of candidates) {
//                     const id = getProductId(item);
//                     const href = getProductHref(item);
//                     if ((id && seenIds.has(id)) || (href && seenHrefs.has(href))) continue;
//                     if (id) seenIds.add(id);
//                     if (href) seenHrefs.add(href);
//                     frag.appendChild(item);
//                 }

//                 if (frag.childNodes.length) grid.appendChild(frag);

//                 // Następny URL z nowej paginacji
//                 const fresh = getNextUrl(doc);
//                 nextUrl = fresh || null;
//                 if (!nextUrl) {
//                     done = true;
//                     sentinel.style.display = 'none';
//                     io.disconnect();
//                 }

//                 // Zapis po dociągnięciu
//                 saveState();

//                 // Jeśli masz jakieś re-inity (np. swatches), wywołaj je tutaj
//                 // window.initSwatches?.(grid);
//             } catch (e) {
//                 console.error('Infinite scroll error:', e);
//                 done = true;
//                 sentinel.style.display = 'none';
//                 body.classList.remove('js-infinite-enabled');
//             } finally {
//                 loading = false;
//             }
//         },
//         { rootMargin: '600px 0px 800px 0px', threshold: 0 },
//     );

//     io.observe(sentinel);

//     // Respect reduced motion
//     if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
//         io.disconnect();
//         body.classList.remove('js-infinite-enabled');
//         sentinel.style.display = 'none';
//     }
// })();
